import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Loader2, RotateCcw, Save, MessageSquare, Info, Terminal, Plus, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  useBotMessages,
  useUpdateBotMessage,
  useResetBotMessage,
} from '@/hooks/useBotMessages';
import type { BotMessage } from '@/api/bot-messages.api';
import { useCommands, useSaveCommands } from '@/hooks/useCommands';
import type { ChatCommand } from '@/api/commands.api';

export const Route = createFileRoute('/_authenticated/mensajes')({
  component: MensajesPage,
});

const CATEGORY_ORDER = ['Bienvenida', 'Carga', 'Retiro', 'Soporte', 'Cuponera', 'General', 'Error'];

const CATEGORY_VARIANT: Record<string, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  Bienvenida: 'success',
  Carga: 'default',
  Retiro: 'warning',
  Soporte: 'secondary',
  Cuponera: 'success',
  General: 'secondary',
  Error: 'destructive',
};

type MensajesTab = 'mensajes' | 'comandos';

function MensajesPage() {
  const [tab, setTab] = useState<MensajesTab>('mensajes');

  return (
    <>
      <TopBanner
        title="Mensajes del bot"
        subtitle="Editá los textos automáticos y los comandos rápidos del chat"
      />

      <div className="flex-1 overflow-auto p-6">
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setTab('mensajes')}
            className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              tab === 'mensajes'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background border hover:bg-accent'
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            💬 Mensajes
          </button>
          <button
            type="button"
            onClick={() => setTab('comandos')}
            className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              tab === 'comandos'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background border hover:bg-accent'
            }`}
          >
            <Terminal className="h-4 w-4" />
            ⌨️ Comandos
          </button>
        </div>

        {tab === 'mensajes' && <BotMessagesTab />}
        {tab === 'comandos' && <CommandsTab />}
      </div>
    </>
  );
}

function BotMessagesTab() {
  const { data: messages = [], isLoading } = useBotMessages();

  const grouped = useMemo(() => {
    const by: Record<string, BotMessage[]> = {};
    for (const m of messages) (by[m.category] ??= []).push(m);
    return CATEGORY_ORDER.filter((c) => by[c]?.length).map((c) => ({ category: c, items: by[c] }));
  }, [messages]);

  const overriddenCount = messages.filter((m) => m.isOverridden).length;

  return (
    <>
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          Estos son los mensajes automáticos del bot del chat. Usá variables como{' '}
          <code className="rounded bg-blue-100 px-1">{'{{username}}'}</code> y se reemplazan solas.
          {overriddenCount > 0 && (
            <> Tenés <strong>{overriddenCount}</strong> mensaje(s) personalizado(s).</>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando mensajes…
        </div>
      )}

      {!isLoading &&
        grouped.map(({ category, items }) => (
          <div key={category} className="mb-6">
            <div className="mb-2 flex items-center gap-2">
              <Badge variant={CATEGORY_VARIANT[category] || 'secondary'}>{category}</Badge>
              <span className="text-xs text-muted-foreground">{items.length} mensaje(s)</span>
            </div>
            <div className="grid gap-3">
              {items.map((m) => (
                <BotMessageCard key={m.key} message={m} />
              ))}
            </div>
          </div>
        ))}
    </>
  );
}

function BotMessageCard({ message }: { message: BotMessage }) {
  const [text, setText] = useState(message.body);
  const updateMutation = useUpdateBotMessage();
  const resetMutation = useResetBotMessage();

  // Re-sincronizar cuando cambia el body desde el server (guardado/reset).
  useEffect(() => {
    setText(message.body);
  }, [message.body]);

  const dirty = text.trim() !== message.body.trim();
  const empty = !text.trim();
  const busy = updateMutation.isPending || resetMutation.isPending;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-semibold">{message.label}</span>
            {message.isOverridden && (
              <Badge variant="warning" className="text-[10px]">Personalizado</Badge>
            )}
          </div>
          <code className="text-[11px] text-muted-foreground">{message.key}</code>
        </div>

        <textarea
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={busy}
        />

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1">
            {message.variables.length > 0 ? (
              message.variables.map((v) => (
                <button
                  key={v}
                  type="button"
                  className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted/70"
                  title="Insertar variable"
                  onClick={() => setText((t) => `${t}{{${v}}}`)}
                >
                  {`{{${v}}}`}
                </button>
              ))
            ) : (
              <span className="text-[11px] text-muted-foreground">Sin variables</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {message.isOverridden && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => resetMutation.mutate(message.key)}
                title="Volver al texto original"
              >
                {resetMutation.isPending ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="mr-1 h-3.5 w-3.5" />
                )}
                Restaurar
              </Button>
            )}
            <Button
              size="sm"
              disabled={busy || !dirty || empty}
              onClick={() => updateMutation.mutate({ key: message.key, body: text })}
            >
              {updateMutation.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="mr-1 h-3.5 w-3.5" />
              )}
              Guardar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CommandsTab() {
  const { data: commands = [], isLoading } = useCommands();
  const saveMutation = useSaveCommands();
  const [editing, setEditing] = useState<ChatCommand | 'new' | null>(null);
  const [form, setForm] = useState({ name: '', command: '', message: '', active: true });

  const openNew = () => {
    setForm({ name: '', command: '/', message: '', active: true });
    setEditing('new');
  };

  const openEdit = (c: ChatCommand) => {
    setForm({ name: c.name, command: c.command, message: c.message, active: c.active });
    setEditing(c);
  };

  const closeForm = () => {
    setEditing(null);
    setForm({ name: '', command: '', message: '', active: true });
  };

  const handleSave = () => {
    const name = form.name.trim();
    const message = form.message.trim();
    let command = form.command.trim();
    if (!command.startsWith('/')) command = `/${command}`;
    if (!name || command === '/' || !message) {
      toast.error('Completá nombre, comando y mensaje');
      return;
    }

    let next: ChatCommand[];
    if (editing && editing !== 'new') {
      next = commands.map((c) =>
        c.id === editing.id ? { ...c, name, command, message, active: form.active } : c,
      );
    } else {
      const newCmd: ChatCommand = {
        id: crypto.randomUUID(),
        name,
        command,
        message,
        active: form.active,
      };
      next = [...commands, newCmd];
    }
    saveMutation.mutate(next, { onSuccess: closeForm });
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('¿Eliminar este comando?')) return;
    saveMutation.mutate(commands.filter((c) => c.id !== id));
  };

  const handleToggleActive = (c: ChatCommand) => {
    saveMutation.mutate(commands.map((x) => (x.id === c.id ? { ...x, active: !x.active } : x)));
  };

  const columns = useMemo<ColumnDef<ChatCommand>[]>(
    () => [
      {
        accessorKey: 'command',
        header: 'Comando',
        cell: ({ row }) => (
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.original.command}</code>
        ),
      },
      { accessorKey: 'name', header: 'Nombre' },
      {
        accessorKey: 'message',
        header: 'Mensaje',
        cell: ({ row }) => (
          <span className="line-clamp-1 text-xs text-muted-foreground">{row.original.message}</span>
        ),
      },
      {
        accessorKey: 'active',
        header: 'Activo',
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => handleToggleActive(row.original)}
            disabled={saveMutation.isPending}
          >
            <Badge variant={row.original.active ? 'success' : 'secondary'} className="cursor-pointer">
              {row.original.active ? 'Activo' : 'Inactivo'}
            </Badge>
          </button>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => openEdit(row.original)} title="Editar">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(row.original.id)}
              disabled={saveMutation.isPending}
              title="Eliminar"
            >
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </div>
        ),
      },
    ],
    [saveMutation.isPending],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          Comandos rápidos para el operador. Al tipear <code className="rounded bg-blue-100 px-1">/</code> en
          un chat aparecen los comandos <strong>activos</strong> como sugerencia.
        </div>
      </div>

      <div className="flex justify-end">
        <Button size="sm" onClick={openNew}>
          <Plus className="mr-1 h-3.5 w-3.5" />
          Nuevo comando
        </Button>
      </div>

      {editing && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="text-sm font-semibold">
              {editing === 'new' ? 'Nuevo comando' : 'Editar comando'}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Nombre</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Consultar saldo"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Comando</label>
                <Input
                  value={form.command}
                  onChange={(e) => setForm((f) => ({ ...f, command: e.target.value }))}
                  placeholder="/saldo"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Mensaje</label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                placeholder="Texto que se inserta en el chat al elegir este comando"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
              Activo (aparece en el autocompletado del chat)
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={closeForm} disabled={saveMutation.isPending}>
                Cancelar
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="mr-1 h-3.5 w-3.5" />
                )}
                Guardar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <DataTable
        columns={columns}
        data={commands}
        isLoading={isLoading}
        pageSize={10}
        emptyMessage="Sin comandos todavía. Creá el primero con 'Nuevo comando'."
      />
    </div>
  );
}
